import { ElementType, Fragment, ReactElement, ReactNode } from 'react'
import { CheckCircle, Error as ErrorIcon, Lock, Schedule, Warning } from '@mui/icons-material'
import { Box, SvgIconProps, Tooltip, useMediaQuery, useTheme } from '@mui/material'

export type LeftRailNavStatus = 'done' | 'warning' | 'error' | 'pending' | 'locked'

export type LeftRailNavSubItem = {
  id: string
  icon: ReactNode
  title: string
  tooltip?: string
  color: string
  onClick?: () => void
}

export type LeftRailNavItem = {
  id: string
  icon: ReactNode
  statuses?: LeftRailNavStatus[]
  subItems?: LeftRailNavSubItem[]
  label?: string
}

export type LeftRailNavProps = {
  items: LeftRailNavItem[]
  value?: string
  onChange?: (id: string) => void
}

const isCypress = typeof window !== 'undefined' && 'Cypress' in window

const STATUS_TYPES: Record<
  LeftRailNavStatus,
  {
    color: string
    icon: ElementType<SvgIconProps>
    title: string
  }
> = {
  done: { color: '#2E7D32', icon: CheckCircle, title: 'Fertig' },
  warning: { color: '#ED6C02', icon: Warning, title: 'Achtung' },
  error: { color: '#D32F2F', icon: ErrorIcon, title: 'Fehler' },
  pending: { color: '#0288D1', icon: Schedule, title: 'In Arbeit' },
  locked: { color: '#616161', icon: Lock, title: 'Gesperrt' },
}

const getLrnTheme = ({ largeScreen }: { largeScreen: boolean }) =>
  ({
    railWidth: isCypress ? 81 : largeScreen ? 52 : 44,
    itemSize: largeScreen ? 42 : 36,
    itemRadius: 4,
    itemActiveBg: 'rgba(55,132,52,0.08)',
    itemActiveFg: '#378434',
    itemIdleFg: 'rgba(0,0,0,0.54)',
    itemHoverBg: 'rgba(0,0,0,0.04)',
    trayWidth: largeScreen ? 24 : 20,
    trayBg: 'rgba(0,0,0,0.04)',
    trayRadius: largeScreen ? 12 : 10,
    trayBorderTop: '1px solid rgba(0,0,0,0.08)',
    separator: 'rgba(0,0,0,0.10)',
    statusIconBoxSize: largeScreen ? 16 : 14,
    statusGlyphSize: largeScreen ? 16 : 14,
    subItemButtonSize: largeScreen ? 20 : 18,
  } as const)

const StatusIcon = ({
  id,
  largeScreen,
}: {
  id: LeftRailNavStatus
  largeScreen: boolean
}): ReactElement | null => {
  const s = STATUS_TYPES[id]
  if (!s) return null
  const LRN_THEME = getLrnTheme({ largeScreen })
  return (
    <Tooltip title={s.title} arrow placement="right">
      <Box
        component="span"
        sx={{
          position: 'relative',
          width: LRN_THEME.statusIconBoxSize,
          height: LRN_THEME.statusIconBoxSize,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: s.color,
          flexShrink: 0,
        }}
      >
        <s.icon sx={{ fontSize: LRN_THEME.statusGlyphSize, lineHeight: 1, flexShrink: 0 }} />
      </Box>
    </Tooltip>
  )
}

const StatusStack = ({
  statuses,
  largeScreen,
}: {
  statuses?: LeftRailNavStatus[]
  largeScreen: boolean
}): ReactElement | null => {
  if (!statuses || statuses.length === 0) return null
  const LRN_THEME = getLrnTheme({ largeScreen })
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: largeScreen ? '4px' : '3px',
        marginTop: largeScreen ? '-1px' : '-2px',
        padding: largeScreen ? '5px 0 4px' : '4px 0 3px',
        width: LRN_THEME.trayWidth,
        background: LRN_THEME.trayBg,
        borderRadius: `${LRN_THEME.trayRadius}px`,
        borderTop: LRN_THEME.trayBorderTop,
      }}
    >
      {statuses.map((sId, i) => (
        <Fragment key={`${sId}-${i}`}>
          {i > 0 ? (
            <Box
              component="span"
              sx={{ width: 10, height: '1px', background: LRN_THEME.separator }}
            />
          ) : null}
          <StatusIcon id={sId} largeScreen={largeScreen} />
        </Fragment>
      ))}
    </Box>
  )
}

const SubItemStack = ({
  subItems,
  largeScreen,
}: {
  subItems?: LeftRailNavSubItem[]
  largeScreen: boolean
}): ReactElement | null => {
  if (!subItems || subItems.length === 0) return null
  const LRN_THEME = getLrnTheme({ largeScreen })
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: largeScreen ? '4px' : '3px',
        marginTop: largeScreen ? '-1px' : '-2px',
        padding: largeScreen ? '5px 0 4px' : '4px 0 3px',
        width: LRN_THEME.trayWidth,
        background: LRN_THEME.trayBg,
        borderRadius: `${LRN_THEME.trayRadius}px`,
        borderTop: LRN_THEME.trayBorderTop,
      }}
    >
      {subItems.map((s, i) => (
        <Fragment key={`${s.id}-${i}`}>
          {i > 0 ? (
            <Box
              component="span"
              sx={{ width: 10, height: '1px', background: LRN_THEME.separator }}
            />
          ) : null}
          <Tooltip title={s.tooltip ?? s.title} arrow placement="right">
            <Box
              component="button"
              type="button"
              onClick={e => {
                e.stopPropagation()
                s.onClick?.()
              }}
              sx={{
                width: LRN_THEME.subItemButtonSize,
                height: LRN_THEME.subItemButtonSize,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: s.color,
                background: 'transparent',
                border: 0,
                padding: 0,
                cursor: s.onClick ? 'pointer' : 'default',
              }}
            >
              {s.icon}
            </Box>
          </Tooltip>
        </Fragment>
      ))}
    </Box>
  )
}

const NavItem = ({
  item,
  active,
  onClick,
  largeScreen,
}: {
  item: LeftRailNavItem
  active: boolean
  onClick: () => void
  largeScreen: boolean
}): ReactElement => {
  const hasStatus = Boolean(item.statuses?.length || item.subItems?.length)
  const LRN_THEME = getLrnTheme({ largeScreen })

  const mainButton = (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: '100%',
        height: `${LRN_THEME.itemSize}px`,
        borderRadius: `${LRN_THEME.itemRadius}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        cursor: 'pointer',
        color: active ? LRN_THEME.itemActiveFg : LRN_THEME.itemIdleFg,
        background: active ? LRN_THEME.itemActiveBg : 'transparent',
        transition: 'background 150ms',
        boxSizing: 'border-box',
        border: 0,
        outline: 0,
        font: 'inherit',
        padding: 0,
        '&:hover': { background: active ? LRN_THEME.itemActiveBg : LRN_THEME.itemHoverBg },
      }}
    >
      {item.icon}
    </Box>
  )

  return (
    <Box
      sx={{
        width: LRN_THEME.itemSize,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: hasStatus ? '4px' : 0,
        boxSizing: 'border-box',
      }}
    >
      {item.label ? (
        <Tooltip title={item.label} arrow placement="right">
          {mainButton}
        </Tooltip>
      ) : (
        mainButton
      )}
      {item.subItems ? (
        <SubItemStack subItems={item.subItems} largeScreen={largeScreen} />
      ) : (
        <StatusStack statuses={item.statuses} largeScreen={largeScreen} />
      )}
    </Box>
  )
}

const LeftRailNav = ({ items, value, onChange }: LeftRailNavProps): ReactElement => {
  const theme = useTheme()
  const largeScreen = useMediaQuery('(min-width: 1920px)')
  const LRN_THEME = getLrnTheme({ largeScreen })
  return (
    <Box
      sx={{
        width: `${LRN_THEME.railWidth}px`,
        background: '#fff',
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: largeScreen ? '6px 0' : '4px 0',
        gap: 0,
        flexShrink: 0,
      }}
    >
      {items.map((it, idx) => (
        <Fragment key={it.id}>
          <NavItem
            item={it}
            active={it.id === value}
            onClick={() => onChange?.(it.id)}
            largeScreen={largeScreen}
          />
          {idx < items.length - 1 ? (
            <Box
              aria-hidden="true"
              sx={{
                width: largeScreen ? 26 : 22,
                height: '1px',
                background: theme.palette.divider,
                opacity: 0.6,
                my: largeScreen ? '8px' : '6px',
              }}
            />
          ) : null}
        </Fragment>
      ))}
    </Box>
  )
}

export default LeftRailNav
